import React, { FunctionComponent, useContext, useState } from "react";
import { RouteComponentProps, withRouter } from "react-router";
import { Button } from "@cmsgov/ds-medicare-gov";
import { useTranslate } from "../../helpers/intlHooks";
import {
  ActionType,
  County,
  PlanType,
  Beneficiary,
  PharmacyType,
} from "../../@types";
import { CountyPicker } from "../../components";
import { Routes } from "../../app";
import { AppStore } from "../../app/store";
import { CoverageType } from "./types";
import LISQuestions from "./LISQuestions/LISQuestions";
import URI from "urijs";
import { sendTealiumEvent } from "../../helpers";
import states from "../EnrollmentFormPage/states";

interface CountyAndMaybeLisProps extends RouteComponentProps {
  coverageType: CoverageType;
  beneficiary?: Beneficiary;
}

const CountyAndMaybeLis: FunctionComponent<CountyAndMaybeLisProps> = ({
  coverageType,
  beneficiary,
  location,
  history,
}) => {
  const t = useTranslate();
  const [county, setCounty] = useState<County | undefined>(undefined);
  const { dispatch, state } = useContext(AppStore.AppContext);

  const fips = county ? county.fips : "0";
  const planType =
    coverageType === CoverageType.MAPD
      ? PlanType.MAPD.toLowerCase()
      : PlanType.PDP.toLowerCase();
  const searchPreferencesUrl = `${Routes.drugSearchPreferences}?fips=${fips}&plan_type=${planType}`;

  const handleCountySelected = (newCounty?: County, zipcode?: string): void => {
    setCounty(newCounty);
    if (newCounty) {
      dispatch({ type: ActionType.UPDATE_COUNTY, payload: newCounty });
      dispatch({ type: ActionType.UPDATE_FIPS, payload: newCounty.fips });
    }
    if (zipcode) {
      /* istanbul ignore next: tested elsewhere */
      sendTealiumEvent("Find Plans - Choose Plans", "Enter Zip Code");
      dispatch({ type: ActionType.UPDATE_ZIPCODE, payload: zipcode });
      dispatch({ type: ActionType.UPDATE_PHARMACY_ADDRESS, payload: "" });
    }
  };

  const handleContinuing = (): void => {
    dispatch({
      type: ActionType.UPDATE_PLANTYPE,
      payload: coverageType == CoverageType.MAPD ? PlanType.MAPD : PlanType.PDP,
    });

    dispatch({ type: ActionType.UPDATE_CW_COVERAGE, payload: undefined });

    if (state.prescriptions.length) {
      dispatch({
        type: ActionType.UPDATE_PHARMACY_TYPE,
        payload:
          state.pharmacyType === PharmacyType.NONE ||
          state.pharmacyType === PharmacyType.RETAIL
            ? PharmacyType.RETAIL
            : PharmacyType.MAIL_AND_RETAIL,
      });

      history.push(
        `${Routes.prescriptionsList}?fips=${fips}&plan_type=${planType}`
      );
    } else {
      history.push(searchPreferencesUrl);
    }
  };

  const renderAnonymous = (): JSX.Element => {
    const uri = new URI(location.search);
    const { fips: urlFips, zip: urlZip } = uri.search(true);

    return (
      <div className="ds-u-margin-top--5">
        <CountyPicker
          label={t("plan_search.enter_your_zip_code")}
          onCountySelect={handleCountySelected}
          initialFips={urlFips}
          initialZip={urlZip}
        />

        {county && <LISQuestions nextPage={handleContinuing} />}
      </div>
    );
  };

  const renderAuthenticated = (bene: Beneficiary): JSX.Element => {
    const uri = new URI(location.search);
    const { fips: urlFips, zip: urlZip } = uri.search(true);
    const stateInfo = states.find(
      s => s.abbreviation === bene.mailing_address.state
    );
    const initialFips =
      urlFips ||
      (stateInfo ? `${stateInfo.fipsCode}${bene.mailing_address.county}` : "");
    const initialZip = urlZip || bene.mailing_address.zipcode;
    const showContinue = !!county;

    return (
      <>
        <CountyPicker
          label={t("plan_search.enter_your_zip_code")}
          onCountySelect={handleCountySelected}
          initialFips={initialFips}
          initialZip={initialZip}
        />

        {showContinue && (
          <section data-testid="continue" className="ContinueSection">
            <Button
              variation="primary"
              onClick={(): void => {
                sendTealiumEvent("Find Plans", "Continue");
                handleContinuing();
              }}
            >
              {t("landing_page.continue")}
            </Button>
          </section>
        )}
      </>
    );
  };

  if (beneficiary) {
    return renderAuthenticated(beneficiary);
  } else {
    return renderAnonymous();
  }
};

export default withRouter(CountyAndMaybeLis);
